import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Map } from 'immutable';
import MailCheck from 'react-mailcheck';
import cx from 'classnames';

import { formatPhoneNumber } from 'lib/common/format-phone-number';
import env from 'lib/env';

import updateUser from 'action-creators/account/update-user';
import dismissError from 'action-creators/account/dismiss-error';
import trackEventCreator from 'action-creators/analytics/track-event';

import User from 'models/user';
import Brand from 'models/brand';

import AccountHeadline from 'components/common/account/account-headline';

const INPUT_CHANGE_TIMEOUT = 500;
const { GOOGLE_OAUTH_ID } = env();

const propTypes = {
  user: PropTypes.instanceOf(User).isRequired,
  brand: PropTypes.instanceOf(Brand).isRequired,
  errors: PropTypes.instanceOf(Map).isRequired,
  updateUser: PropTypes.func.isRequired,
  dismissError: PropTypes.func.isRequired,
  app: PropTypes.string.isRequired,
  trackEvent: PropTypes.func.isRequired,
};

class AccountSettings extends Component {
  constructor(props) {
    super(props);

    const {
      firstname,
      lastname,
      email,
      phone,
      feedbackReminder,
    } = props.user;

    this.state = {
      firstname,
      lastname,
      email,
      phone,
      feedbackReminder,
      password: null,
      passwordChanged: false,
      shouldShowPasswordField: false,
      googleSignedIn: false,
    };

    this.onSubmit = this.onSubmit.bind(this);
    this.onFirstnameChange = this.onFirstnameChange.bind(this);
    this.onLastnameChange = this.onLastnameChange.bind(this);
    this.onEmailChange = this.onEmailChange.bind(this);
    this.onPhoneNumberChange = this.onPhoneNumberChange.bind(this);
    this.onPasswordChange = this.onPasswordChange.bind(this);
    this.useEmailSuggestion = this.useEmailSuggestion.bind(this);
    this.showPasswordField = this.showPasswordField.bind(this);
    this.renderShowPasswordButton = this.renderShowPasswordButton.bind(this);
    this.toggleFeedbackReminder = this.toggleFeedbackReminder.bind(this);
    this.onGoogleDisconnect = this.onGoogleDisconnect.bind(this);
  }

  componentDidMount() {
    if (window.gapi) {
      window.gapi.load('auth2', () => {
        window.gapi.auth2.init({ client_id: GOOGLE_OAUTH_ID });
        window.gapi.auth2.getAuthInstance().then(() => {
          if (window.gapi.auth2.getAuthInstance().isSignedIn.get()) {
            this.setState({ googleSignedIn: true });
          }
        });
      });
    }
  }

  showPasswordField(event) {
    event.preventDefault();
    event.stopPropagation();

    this.setState({ shouldShowPasswordField: true });
  }

  onSubmit(e) {
    e.preventDefault();
    e.stopPropagation();

    // Because email changes on blur, we need to more agressively capture it
    const email = this.emailInput.value;
    this.props.updateUser({ ...this.state, email });
  }

  onPhoneNumberChange() {
    clearTimeout(this.phoneNumberChangeTimeout);
    let newNumber = this.phoneInput.value.replace(/\D/g, '');
    const prevNumber = this.state.phone;
    const lastChar = prevNumber ? formatPhoneNumber(prevNumber).slice(-1)[0] : '';
    const maxLength = newNumber[0] === '1' ? 11 : 10;

    // To handle case of deleting pretty chars ( '(', ')', '-' ) etc
    if (prevNumber === newNumber && lastChar.match(/[()\-+ ]/)) { newNumber = newNumber.slice(0, -1); }
    // Don't let user enter more numbaZ
    newNumber = newNumber.substring(0, maxLength);
    this.setState({ phone: newNumber });
    this.props.dismissError({ error: 'phone' });
    this.phoneNumberChangeTimeout = setTimeout(this.onPhoneNumberChange, INPUT_CHANGE_TIMEOUT);
  }

  onFirstnameChange(e) {
    e.preventDefault();
    e.stopPropagation();
    const firstname = this.firstnameInput.value;
    this.setState({ firstname });
    this.props.dismissError({ error: 'firstname' });
  }

  onLastnameChange(e) {
    e.preventDefault();
    e.stopPropagation();
    const lastname = this.lastnameInput.value;
    this.setState({ lastname });
    this.props.dismissError({ error: 'lastname' });
  }

  onPasswordChange(e) {
    e.preventDefault();
    e.stopPropagation();
    const password = this.passwordInput.value;
    this.setState({ password, passwordChanged: true });
    this.props.dismissError({ error: 'password' });
  }

  onEmailChange(e) {
    e.preventDefault();
    e.stopPropagation();
    this.props.dismissError({ error: 'email' });
  }

  onEmailBlur(e) {
    e.preventDefault();
    e.stopPropagation();
    const email = this.emailInput.value;
    this.setState({ email });
  }

  useEmailSuggestion() {
    const suggestion = this.mailcheck.state.suggestion.full;
    this.emailInput.value = suggestion;
    this.setState({ email: suggestion });
  }

  toggleFeedbackReminder() {
    this.setState({ feedbackReminder: !this.state.feedbackReminder });
  }

  onGoogleDisconnect() {
    window.gapi.auth2.getAuthInstance().disconnect();
    this.setState({ googleSignedIn: false });
  }

  renderEmailSuggestion() {
    return (
      <MailCheck ref={(mailcheck) => { this.mailcheck = mailcheck; }} email={this.state.email}>
        {(suggestion) => {
          if (suggestion) {
            return (<div className="email-suggestion">Did you mean {<span className="suggestion-value" onClick={this.useEmailSuggestion}>{suggestion.full}</span>}?</div>);
          }
          return null;
        }}
      </MailCheck>
    );
  }

  renderFirstnameField() {
    const isValid = this.props.errors.getIn(['firstname', 'isValid'], true);
    const labelClasses = cx({
      'required': true,
      'text-color-red': !isValid,
    });
    const fieldClasses = cx({
      'form-control': true,
      'form-control-error': !isValid,
    });
    return (
      <div className="col-lg-4 col-md-5 form-group">
        <label className={labelClasses} htmlFor="firstname">
          FIRST NAME: <span className="required">*</span>
        </label>
        <div className="form-field-container">
          <input
            type="text"
            id="firstname"
            name="firstname"
            defaultValue={this.state.firstname}
            ref={(firstnameInput) => { this.firstnameInput = firstnameInput; }}
            onChange={this.onFirstnameChange}
            className={fieldClasses}
            autoComplete="given-name"
            placeholder="Your First Name"
          />
        </div>
      </div>
    );
  }

  renderLastnameField() {
    const isValid = this.props.errors.getIn(['lastname', 'isValid'], true);
    const labelClasses = cx({
      'required': true,
      'text-color-red': !isValid,
    });
    const fieldClasses = cx({
      'form-control': true,
      'form-control-error': !isValid,
    });
    return (
      <div className="col-lg-4 col-md-5 form-group">
        <label className={labelClasses} htmlFor="lastname">
          LAST NAME: <span className="required">*</span>
        </label>
        <div className="form-field-container">
          <input
            type="text"
            id="lastname"
            name="lastname"
            defaultValue={this.state.lastname}
            ref={(lastnameInput) => { this.lastnameInput = lastnameInput; }}
            onChange={this.onLastnameChange}
            className={fieldClasses}
            autoComplete="family-name"
            placeholder="Your Last Name"
          />
        </div>
      </div>
    );
  }

  renderEmailField() {
    const isValid = this.props.errors.getIn(['email', 'isValid'], true);
    const labelClasses = cx({
      'required': true,
      'text-color-red': !isValid,
    });
    const fieldClasses = cx({
      'form-control': true,
      'form-control-error': !isValid,
    });
    return (
      <div className="col-lg-8 col-md-10 form-group">
        <label className={labelClasses} htmlFor="email">
          EMAIL: <span className="required">*</span>
        </label>
        <div className="form-field-container">
          <input
            type="text"
            id="email"
            name="email"
            defaultValue={this.state.email}
            ref={(emailInput) => { this.emailInput = emailInput; }}
            onChange={this.onEmailChange}
            onBlur={this.onEmailBlur}
            className={fieldClasses}
            autoComplete="email"
            placeholder="Your Email Address"
          />
          {this.renderEmailSuggestion()}
        </div>
      </div>
    );
  }

  renderPhoneField() {
    const isValid = this.props.errors.getIn(['phone', 'isValid'], true);
    const labelClasses = cx({
      'text-color-red': !isValid,
    });
    const fieldClasses = cx({
      'form-control': true,
      'form-control-error': !isValid,
    });
    return (
      <div className="col-lg-8 col-md-10 form-group">
        <label className={labelClasses} htmlFor="phone">
          PHONE NUMBER:
        </label>
        <div className="form-field-container">
          <input
            type="text"
            id="phone"
            name="phone"
            value={formatPhoneNumber(this.state.phone)}
            ref={(phoneInput) => { this.phoneInput = phoneInput; }}
            onChange={this.onPhoneNumberChange}
            className={fieldClasses}
            autoComplete="tel"
            placeholder="(___) ___-____"
          />
        </div>
      </div>
    );
  }

  renderPasswordField() {
    if (!this.state.shouldShowPasswordField) { return null; }
    const isValid = this.props.errors.getIn(['password', 'isValid'], true);
    const labelClasses = cx({
      'display-inline-block': true,
      'text-color-red': !isValid,
    });
    const fieldClasses = cx({
      'form-control': true,
      'form-control-error': !isValid,
    });
    let errorMessage;
    if (!isValid) {
      errorMessage = (
        <div className="display-inline-block float-right text-color-red text-size-12 text-weight-book">
          Password 8 Characters Minimum
        </div>
      );
    }
    return (
      <div className="row">
        <div className="col-lg-8 col-md-10 form-group">
          <label className={labelClasses} htmlFor="password">
            NEW PASSWORD:
          </label>
          {errorMessage}
          <div className="form-field-container">
            <input
              type="password"
              id="password"
              name="password"
              ref={(passwordInput) => { this.passwordInput = passwordInput; }}
              onChange={this.onPasswordChange}
              className={fieldClasses}
              autoComplete="password"
              placeholder="****************"
            />
          </div>
        </div>
      </div>
    );
  }

  renderShowPasswordButton() {
    if (this.state.shouldShowPasswordField) { return null; }
    return (
      <div className="row">
        <div className="col-xs-12 padding-top-xs-10 padding-bottom-xs-30"><a onClick={this.showPasswordField}>CHANGE PASSWORD</a></div>
      </div>
    );
  }

  renderFeedbackField() {
    const { feedbackReminder } = this.state;
    const feedbackReminderClasses = cx({
      checkbox: true,
      selected: feedbackReminder,
    });

    return (
      <div className="col-xs-12 padding-top-xs-10 padding-bottom-xs-30">
        <div className={feedbackReminderClasses} onClick={this.toggleFeedbackReminder}>
          <label className="text-weight-book noselect">Remind me to leave feedback about where I've parked.</label>
        </div>
      </div>
    );
  }

  renderGoogleSignout() {
    if (!this.state.googleSignedIn) { return null; }

    return (
      <div
        className="col-xs-12 padding-top-xs-10 padding-bottom-xs-10 clickable text-color-blue"
        onClick={this.onGoogleDisconnect}
      >
        Disconnect your Google account.
      </div>
    );
  }

  render() {
    const buttonClasses = cx({
      'btn-block': true,
      'btn-primary': true,
      'text-center': true,
      'btn-bp': this.props.brand.isBestParking,
    });
    return (
      <Fragment>
        <AccountHeadline brand={this.props.brand} headline="PERSONAL PROFILE" trackEvent={this.props.trackEvent} app={this.props.app} />
        <div className="heavy-shadowed area dismissable margin-top-10 margin-bottom-40">
          <div className="row">
            <div className="col-md-11 col-md-offset-1">
              <div className="text-color-dark-slate text-size-xs-12 text-size-sm-16 text-weight-book padding-top-xs-10 padding-bottom-xs-10 padding-top-sm-40 padding-bottom-sm-40">
                Make your parking a breeze by completing your profile. Then we can customize your choices for you.
              </div>
              <form onSubmit={this.onSubmit}>
                <fieldset>
                  <div className="row">
                    {this.renderFirstnameField()}
                    {this.renderLastnameField()}
                  </div>
                  <div className="row">
                    {this.renderEmailField()}
                    {this.renderPhoneField()}
                  </div>
                  <div className="row">
                    {this.renderFeedbackField()}
                  </div>
                  <div className="row">
                    {this.renderGoogleSignout()}
                  </div>
                  {this.renderShowPasswordButton()}
                  {this.renderPasswordField()}
                  <div className="row">
                    <div className="col-lg-4 col-md-5 form-group">
                      <button className={buttonClasses} onSubmit={this.onSubmit}>UPDATE</button>
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  const {
    user,
    errors,
  } = state.account;

  const {
    brand,
  } = state.brand;

  const { name: app } = state.app;

  return {
    user,
    brand,
    errors,
    app,
  };
};

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    updateUser,
    dismissError,
    trackEvent: trackEventCreator,
  }, dispatch)
);

AccountSettings.propTypes = propTypes;
export default connect(mapStateToProps, mapDispatchToProps)(AccountSettings);
